import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, CalendarCheck, Star } from "lucide-react";
import api, { API } from "@/lib/api";
import { track } from "@/lib/analytics";
import BookingDialog from "@/components/BookingDialog";

const genId = () => "aries-" + Math.random().toString(36).slice(2);

export const openAries = () => window.dispatchEvent(new Event("open-aries"));

const SUGGESTIONS = [
  "Aiutami a scegliere il servizio giusto per me",
  "Cosa sono i Registri Akashici?",
  "Vorrei scoprire i miei talenti",
];

const cleanText = (t = "") =>
  t.replace(/\[\[PRENOTA:[^\]]*\]\]/g, "").replace(/\[\[TEMA_NATALE\]\]/g, "").replace(/\[\[[^\]]*$/, "")
    .replace(/\*\*(.*?)\*\*/g, "$1").replace(/[*#`_]/g, "").trim();
const extractSlug = (t = "") => t.match(/\[\[PRENOTA:([a-z0-9-]+)\]\]/)?.[1] || null;
const hasNatal = (t = "") => /\[\[TEMA_NATALE\]\]/.test(t);

export default function AriesWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genId);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Sono ARIES, l'assistente di LUX AETERNA. Posso guidarti in tre domande verso il servizio più adatto a te, oppure rispondere a ogni tua curiosità. Come posso accompagnarti oggi?" },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [services, setServices] = useState([]);
  const [booking, setBooking] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-aries", openHandler);
    return () => window.removeEventListener("open-aries", openHandler);
  }, []);

  useEffect(() => {
    if (!open) return;
    track("aries_open");
    if (!services.length) api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    track("aries_message");
    let acc = "";
    try {
      const res = await fetch(`${API}/aries/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`ARIES ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
      if (extractSlug(acc)) track("aries_recommend", undefined, { service: extractSlug(acc) });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Mi dispiace, si è verificato un problema. Riprova tra poco." };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  const startBooking = (svc) => { setOpen(false); setBooking(svc); };
  const isCourse = booking?.category === "Formazione Lux Academy";

  return (
    <>
      <motion.button
        onClick={() => setOpen(true)}
        data-testid="aries-open"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.5, type: "spring" }}
        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[120] w-14 h-14 rounded-full glass border border-gold/40 flex items-center justify-center text-gold shadow-[0_0_30px_rgba(229,197,142,0.25)]"
        aria-label="Apri ARIES"
      >
        <Sparkles size={22} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 right-6 z-[121] w-[92vw] max-w-[400px] h-[560px] max-h-[80vh] rounded-2xl glass flex flex-col overflow-hidden"
            data-testid="aries-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A1F14]/10">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-gold" />
                <div>
                  <p className="text-sm text-[#2A1F14] font-medium tracking-wide">ARIES</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Assistente Lux Aeterna</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="aries-close" aria-label="Chiudi"><X size={18} className="text-[#2A1F14]/60 hover:text-[#2A1F14]" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
              {messages.map((m, i) => {
                const slug = m.role === "assistant" ? extractSlug(m.content) : null;
                const svc = slug ? services.find((s) => s.slug === slug) : null;
                const natal = m.role === "assistant" && hasNatal(m.content);
                const last = i === messages.length - 1;
                return (
                  <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                    <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-gold text-black rounded-br-sm" : "bg-[#2A1F14]/5 text-[#2A1F14]/90 rounded-bl-sm border border-[#2A1F14]/5"}`}>
                      {cleanText(m.content) || (streaming && last ? "…" : "")}
                    </div>
                    {svc && !(streaming && last) && (
                      <motion.button initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        onClick={() => startBooking(svc)} data-testid="aries-book-btn"
                        className="mt-2 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors shadow-[0_6px_20px_rgba(185,134,63,0.3)]">
                        <CalendarCheck size={15} /> Prenota: {svc.title}
                        {svc.price && <span className="opacity-70 text-xs">· {svc.price}</span>}
                      </motion.button>
                    )}
                    {natal && !(streaming && last) && (
                      <Link to="/tema-natale" onClick={() => setOpen(false)} data-testid="aries-natal-btn"
                        className="mt-2 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/50 text-gold text-sm hover:bg-gold hover:text-black transition-colors">
                        <Star size={15} /> Scopri il tuo Tema Natale
                      </Link>
                    )}
                  </div>
                );
              })}
              {messages.length === 1 && !streaming && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="aries-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="aries-suggestion"
                      className="text-xs px-3 py-1.5 rounded-full border border-gold/40 text-[#2A1F14]/75 hover:border-gold hover:text-gold transition-colors text-left">{s}</button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-[#2A1F14]/10">
              <div className="flex items-center gap-2 bg-[#2A1F14]/5 rounded-full px-4 py-1.5 border border-[#2A1F14]/10">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Scrivi ad ARIES…"
                  data-testid="aries-input"
                  className="flex-1 bg-transparent text-sm text-[#2A1F14] outline-none py-1.5"
                />
                <button onClick={() => send()} disabled={streaming} data-testid="aries-send" className="text-gold disabled:opacity-40" aria-label="Invia">
                  <Send size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {booking && (
        <BookingDialog
          key={booking.slug}
          open
          source="aries"
          onOpenChange={(v) => { if (!v) { setBooking(null); setOpen(true); } }}
          title={`${isCourse ? "Iscrizione" : "Prenota"} — ${booking.title}`}
          endpoint={isCourse ? "enrollments" : "bookings"}
          presets={{ type: isCourse ? "corso" : "servizio", service: booking.title, message: "Consigliato da ARIES." }}
        />
      )}
    </>
  );
}
